import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

export const AdminPublishNews = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin-news/news/all`,
        { withCredentials: true }
      );
      setNews(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load news");
      setLoading(false);
    }
  };

  const handlePublish = async (newsId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin-news/news/publish`,
        { news_id: newsId },
        { withCredentials: true }
      );
      fetchNews(); // Refresh news list
    } catch (err) {
      alert("Failed to publish news");
    }
  };

  const handleUnpublish = async (newsId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin-news/news/unpublish`,
        { news_id: newsId },
        { withCredentials: true }
      );
      fetchNews(); // Refresh news list
    } catch (err) {
      alert("Failed to unpublish news");
    }
  };

  if (loading) return <p style={styles.loading}>Loading news...</p>;
  if (error) return <p style={styles.error}>{error}</p>;

  return (
    <div style={styles.container} className="admin-publish-news">
      <style>{`
        @media (max-width: 1024px) {
          .admin-publish-news { padding: 16px; }
        }
        @media (max-width: 768px) {
          .admin-publish-news { padding: 14px; }
          .admin-publish-header { flex-direction: column; align-items: flex-start; gap: 10px; }
          .admin-publish-actions { width: 100%; display: flex; gap: 8px; flex-wrap: wrap; }
          .admin-publish-actions button { flex: 1; min-width: 140px; }
          .admin-publish-buttons { flex-direction: column; align-items: stretch; }
          .admin-publish-buttons button { width: 100%; }
        }
        @media (max-width: 480px) {
          .admin-publish-news { padding: 12px; }
        }
      `}</style>
      {/* Header with buttons */}
      <div style={styles.header} className="admin-publish-header">
        <h2 style={styles.heading}>Manage News</h2>
        <div style={styles.buttonContainer} className="admin-publish-actions">
          <button
            onClick={() => navigate("/DsportAdmin")}
            style={styles.backButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5a6268")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6c757d")}
          >
            Back
          </button>
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c0392b")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#e74c3c")}
          >
            Logout
          </button>
        </div>
      </div>
      {news.length === 0 ? (
        <p style={styles.noNews}>No news available</p>
      ) : (
        <div style={styles.newsList}>
          {news.map((item) => (
            <div key={item.id} style={styles.newsItem}>
              <div style={styles.newsHeader}>
                <h3 style={styles.title}>{item.title}</h3>
                <div style={styles.metaInfo}>
                  {item.sport_name && (
                    <span style={styles.sportBadge}>🏆 {item.sport_name}</span>
                  )}
                  <span style={styles.clicksBadge}>
                    👆 {item.clicks || 0} clicks
                  </span>
                  <span style={styles.dateBadge}>
                    📅 {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div style={styles.mediaInfo}>
                {item.image_count > 0 && (
                  <span style={styles.mediaBadge}>
                    🖼️ {item.image_count} image
                    {item.image_count !== 1 ? "s" : ""}
                  </span>
                )}
                {item.has_video && (
                  <span style={styles.mediaBadge}>🎥 Video included</span>
                )}
              </div>

              <div style={styles.contentPreview}>
                {item.content.length > 200
                  ? `${item.content.substring(0, 200)}...`
                  : item.content}
              </div>

              {item.references?.length > 0 && (
                <div style={styles.references}>
                  <strong>References ({item.references.length}):</strong>
                  <ul>
                    {item.references.slice(0, 3).map((ref, index) => (
                      <li key={index}>
                        <a
                          href={ref}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.link}
                        >
                          {ref.length > 50 ? `${ref.substring(0, 50)}...` : ref}
                        </a>
                      </li>
                    ))}
                    {item.references.length > 3 && (
                      <li style={{ color: "#666", fontStyle: "italic" }}>
                        ...and {item.references.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              )}

          <div style={styles.buttons} className="admin-publish-buttons">
                <button
                  onClick={() => navigate(`/display-admin-news/${item.id}`)}
                  style={styles.previewBtn}
                >
                  Preview Full Article
                </button>
                {item.approved ? (
                  <button
                    onClick={() => handleUnpublish(item.id)}
                    style={styles.unpublishBtn}
                  >
                    Unpublish
                  </button>
                ) : (
                  <button
                    onClick={() => handlePublish(item.id)}
                    style={styles.publishBtn}
                  >
                    Publish
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Inline CSS styles
const styles = {
  container: {
    padding: "20px",
    maxWidth: "800px",
    margin: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "10px",
    borderBottom: "1px solid #eee",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: "0",
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
  },
  backButton: {
    backgroundColor: "#6c757d",
    color: "white",
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  logoutButton: {
    backgroundColor: "#e74c3c",
    color: "white",
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  noNews: {
    color: "#777",
    fontSize: "16px",
  },
  newsList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  newsItem: {
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxShadow: "2px 2px 10px rgba(0,0,0,0.1)",
  },
  newsHeader: {
    marginBottom: "10px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  metaInfo: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "10px",
  },
  sportBadge: {
    backgroundColor: "#e9ecef",
    color: "#495057",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  clicksBadge: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  dateBadge: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  mediaInfo: {
    display: "flex",
    gap: "8px",
    marginBottom: "10px",
  },
  mediaBadge: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  contentPreview: {
    marginBottom: "10px",
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.4",
    backgroundColor: "#f8f9fa",
    padding: "10px",
    borderRadius: "4px",
    fontFamily: "monospace",
  },
  references: {
    marginTop: "10px",
    fontSize: "14px",
  },
  link: {
    color: "#007BFF",
    textDecoration: "underline",
  },
  buttons: {
    marginTop: "15px",
    display: "flex",
    gap: "10px",
  },
  previewBtn: {
    backgroundColor: "#6c757d",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px",
  },
  publishBtn: {
    backgroundColor: "#28a745",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  unpublishBtn: {
    backgroundColor: "#dc3545",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  loading: {
    fontSize: "16px",
    textAlign: "center",
  },
  error: {
    fontSize: "16px",
    color: "red",
    textAlign: "center",
  },
};

export default AdminPublishNews;
